import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../services/api';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check dacă user e deja logat (la page load)
  // Adaugă interceptor pentru 401/403
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const savedUser = localStorage.getItem('user');

    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (e) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
      // Refresh user data din server (în caz că flag-uri ca isCadruTehnicPSI au fost modificate)
      api
        .get('/auth/me')
        .then((res) => {
          localStorage.setItem('user', JSON.stringify(res.data));
          setUser(res.data);
        })
        .catch(() => {});
    }

    setLoading(false); // ← fără asta, loading rămâne true pentru totdeauna
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await authAPI.login({ email, password });
      const { token, user: userData } = response.data;

      // Salvează în localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Eroare la autentificare';
      setError(message);
      return { success: false, error: message };
    }
  };

  const register = async (formData) => {
    try {
      setError(null);
      const response = await authAPI.register(formData);
      const { token, user: userData } = response.data;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));

      setUser(userData);
      return { success: true };
    } catch (err) {
      const message = err.response?.data?.error || 'Eroare la înregistrare';
      setError(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    // Resetează tema la light la deconectare — sesiunea nouă începe curat.
    try {
      sessionStorage.removeItem('platforma_ssm_theme');
      // Curăță flag-urile de "amânare" onboarding — altfel un admin care
      // resetează CNP/semnătura/traseul unui user rămâne fără efect vizibil,
      // fiindcă flag-ul de skip din sessionStorage supraviețuiește logout-ului.
      if (user?.id) {
        sessionStorage.removeItem(`signatureSkipped_${user.id}`);
        sessionStorage.removeItem(`cnpSkipped_${user.id}`);
        sessionStorage.removeItem(`routeSkipped_${user.id}`);
      }
    } catch {}
    setUser(null);
    window.location.href = '/login';
  };

  // SUPER_ADMIN impersonation: loghează-te ca un alt user fără a-i ști parola.
  // Înlocuiește tokenul curent; la refresh apare banner-ul de impersonare și butonul de revenire.
  const impersonate = async (userId) => {
    try {
      const res = await authAPI.impersonate(userId);
      const { token, user: userData } = res.data;
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      // Reload ca toate paginile/componentele să se re-evalueze cu noul user
      window.location.href = '/dashboard';
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Eroare la impersonare' };
    }
  };

  const stopImpersonation = async () => {
    try {
      const res = await authAPI.stopImpersonation();
      const { token, user: userData } = res.data;
      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      window.location.href = '/dashboard';
      return { success: true };
    } catch (err) {
      return { success: false, error: err.response?.data?.error || 'Eroare la revenire' };
    }
  };

  const hasRole = (allowedRoles) => {
    if (!user) return false;
    return allowedRoles.includes(user.role);
  };

  // Re-fetch user din server — folosit după schimbări care afectează user.company
  // (ex: upload logo firmă, update flag-uri, schimbare rol).
  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
      localStorage.setItem('user', JSON.stringify(res.data));
      setUser(res.data);
      return res.data;
    } catch (_) {
      return null;
    }
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    refreshUser,
    impersonate,
    stopImpersonation,
    isAuthenticated: !!user,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
